/* ------------------------------------------------------------------ */
/* DEMO DATA — Fabricated mock data for the public demo page          */
/* This is NOT real provider data. All values are synthetic.          */
/* ------------------------------------------------------------------ */

export const DEMO_TOKEN_DATA = {
  /* ---- Token basics ---- */
  tokenName: 'PepeCoin',
  tokenSymbol: 'PEPE',
  chain: 'eth',
  chainLabel: 'Ethereum',
  chainColor: '#627eea',
  contractAddress: '0xdEm06982508145454Ce325dDbE47a25d4ec3d231',

  /* ---- Risk assessment ---- */
  riskLevel: 'YELLOW' as const,
  riskScore: 4.8,
  riskLabel: 'Moderate Risk Indicators',
  riskSummary: 'MODERATE_RISK' as const,
  confidence: 0.82,
  headline: 'Moderate-risk memecoin with strong community traction but some holder concentration concerns.',

  /* ---- Summary ---- */
  summary:
    'PepeCoin shows a healthy liquidity profile with 85% of LP locked for 730 days via Unicrypt. ' +
    'Contract analysis reveals low sellability risk (score 1.2/10) with standard 1% buy/sell taxes. ' +
    'The top 10 holders control 12.3% of supply, which is acceptable but warrants monitoring. ' +
    'Smart money wallets show net accumulation over the past 24 hours with a bullish conviction signal. ' +
    'Two minor concerns flagged: an unaudited contract and a slight uptick in deployer-adjacent wallet activity.',

  /* ---- Sellability / Contract Analysis ---- */
  contractSafety: {
    score: 12,
    buyTaxPct: 1.0,
    sellTaxPct: 1.0,
    maxTxPct: 2.0,
    maxWalletPct: 3.0,
    hasMintFunction: false,
    hasBlacklist: false,
    hasPauseFunction: false,
    ownershipRenounced: true,
  },

  /* ---- Liquidity ---- */
  liquidity: {
    totalLiquidityUsd: 12_400_000,
    lpLocked: true,
    lpLockMonths: 24,
    lpLockPlatform: 'Unicrypt',
    initialLiquidityUsd: 8_200_000,
    currentLiquidityUsd: 12_400_000,
    liquidityChange24h: 3.2,
    poolCount: 4,
    depthScore: 8.2,
  },

  /* ---- Holder Distribution ---- */
  holders: {
    totalHolders: 234_567,
    top10HoldersPct: 12.3,
    top20HoldersPct: 18.7,
    devHoldsPct: 0.8,
    burnedPct: 42.1,
    whaleCount: 45,
    concentration: 'LOW' as const,
  },

  /* ---- Smart Money Signals ---- */
  smartMoney: {
    smartMoneyCount: 12,
    whaleCount: 45,
    clusterConviction: 7.4,
    smartBuys24h: 12,
    smartSells24h: 3,
    netFlow24h: 2_100_000,
    signal: 'BULLISH' as const,
    holders: [
      { address: '0x1a2B3c4D5e6F7890AbCdEf1234567890aBcDeF01', type: 'whale', label: 'Early Accumulator', conviction: 8.2 },
      { address: '0x2b3C4d5E6f7890AbCdEf1234567890aBcDeF0123', type: 'smart_money', label: 'DEX Arbitrageur', conviction: 7.8 },
      { address: '0x3c4D5e6F7890AbCdEf1234567890aBcDeF012345', type: 'whale', label: 'Institutional Wallet', conviction: 9.1 },
      { address: '0x4d5E6f7890AbCdEf1234567890aBcDeF01234567', type: 'smart_money', label: 'Alpha Caller', conviction: 6.5 },
      { address: '0x5e6F7890AbCdEf1234567890aBcDeF0123456789', type: 'whale', label: 'LP Provider', conviction: 7.0 },
    ],
  },

  /* ---- Network Analysis ---- */
  network: {
    connectedEntities: 1_234,
    suspiciousLinks: 2,
    exchangeFlows: 'Normal' as const,
    dexActivity: 'High' as const,
    upstreamFunders: [
      { address: '0xA1b2C3d4E5f67890aBcDeF1234567890AbCdEf01', label: 'Binance Hot Wallet' },
      { address: '0xB2c3D4e5F67890aBcDeF1234567890AbCdEf0123', label: 'Uniswap Router' },
      { address: '0xC3d4E5f67890aBcDeF1234567890AbCdEf012345', label: null },
    ],
    downstreamDistributors: [
      { address: '0xD4e5F67890aBcDeF1234567890AbCdEf01234567', label: 'Market Maker A' },
      { address: '0xE5f67890aBcDeF1234567890AbCdEf0123456789', label: null },
    ],
  },

  /* ---- Exploit & Contract Flags ---- */
  contractFlags: {
    sourceVerified: true,
    proxyContract: false,
    knownExploits: 0,
    auditStatus: 'Unaudited' as const,
  },

  /* ---- Signals ---- */
  signals: [
    { type: 'Accumulation', severity: 'info' as const, message: 'Smart money wallets increased holdings by 3.2% in past 24h' },
    { type: 'Liquidity', severity: 'info' as const, message: 'LP lock renewed for additional 12 months on Unicrypt' },
    { type: 'Holder Warning', severity: 'warning' as const, message: 'Wallet 0x3c4D...2345 accumulated 1.8% of supply in 48h' },
    { type: 'Social Spike', severity: 'info' as const, message: 'X mentions up 240% in past 6 hours' },
  ],

  /* ---- Risks & Positives ---- */
  risks: [
    'Contract has not been audited by a recognized firm',
    'Minor holder concentration: one wallet accumulated 1.8% in 48h',
    'Two suspicious address links detected in network analysis',
  ],
  positives: [
    'Ownership renounced — no admin control over contract',
    '85% of LP locked for 730 days via Unicrypt',
    'Low buy/sell tax (1%/1%) with no hidden fees detected',
    'Strong community traction: 234K+ holders',
    'Smart money showing net bullish accumulation',
    'No mint, blacklist, or pause functions in contract',
  ],

  /* ---- Explain Chain ---- */
  explainChain: [
    '3 data sources contributed to this analysis',
    'No sell restrictions detected (score 1.2/10)',
    'Liquidity adequately locked — 85% via Unicrypt for 730 days',
    'Minor holder concentration concern — 1 wallet at 1.8%',
    'Smart money net flow: +.1M (bullish signal)',
    'Risk level: YELLOW (4.8/10, below RED threshold of 7.0)',
  ],

  /* ---- Social Metrics ---- */
  social: {
    xMentionsPerHour: 142,
    xFollowers: 87_500,
    xEngagementRate: 4.2,
    telegramMembers: 12_340,
    telegramGrowthRate: 8.5,
    discordMembers: 5_670,
    websiteAgeDays: 312,
    githubCommits30d: 0,
  },

  /* ---- Meta ---- */
  meta: {
    source: 'demo-mock',
    latencyMs: 0,
    cached: false,
    analysisVersion: 'v2.1-demo',
  },
};
